// ============================================================================
// Sub Onboarding Portal link section (N3, 2026-06-10). Partner records only.
// Issues a tokened portal link via the sub-onboard Edge Function, shows
// delivery state (manual-link fallback when email isn't configured), and
// surfaces per-step portal progress from portal_status.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, Loader2, Send } from 'lucide-react';
import {
  PortalError,
  portalInternal,
  type IssueResult,
  type PortalSteps,
} from '../sub-onboard-portal/lib';
import { errorMessage } from '../../shared/errors';

interface TokenSummary {
  token_id: string;
  recipient_email: string | null;
  created_at: string;
  expires_at: string;
  verified_at: string | null;
  consumed_at: string | null;
}

interface Props {
  onboardingId: string;
  defaultEmail?: string | null;
  canWrite: boolean;
}

const STEP_LABELS: Array<{ key: keyof PortalSteps; label: string }> = [
  { key: 'business_confirmed', label: 'Business confirmed' },
  { key: 'coi_uploaded', label: 'COI' },
  { key: 'w9_uploaded', label: 'W-9' },
  { key: 'direct_deposit_submitted', label: 'ACH authorization' },
  { key: 'agreement_signed', label: 'Agreement signed' },
];

export default function PortalLinkSection({ onboardingId, defaultEmail, canWrite }: Props) {
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [steps, setSteps] = useState<PortalSteps | null>(null);
  const [tokens, setTokens] = useState<TokenSummary[]>([]);

  const refresh = useCallback(async () => {
    try {
      const res = await portalInternal<{ steps: PortalSteps; tokens: TokenSummary[] }>({
        action: 'portal_status',
        onboarding_id: onboardingId,
      });
      setSteps(res.steps);
      setTokens(res.tokens);
    } catch {
      /* best-effort */
    }
  }, [onboardingId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleIssue() {
    setBusy(true);
    setError(null);
    setManualUrl(null);
    try {
      const res = await portalInternal<IssueResult>({
        action: 'issue',
        onboarding_id: onboardingId,
        recipient_email: email.trim() || null,
      });
      if (!res.delivery.delivered && res.portal_url) setManualUrl(res.portal_url);
      await refresh();
    } catch (err) {
      setError(
        err instanceof PortalError && err.message !== err.code
          ? err.message
          : errorMessage(err, 'Failed to issue the portal link.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const activeToken = tokens.find(
    (tk) => !tk.consumed_at && new Date(tk.expires_at).getTime() > Date.now(),
  );

  return (
    <section className="mt-4 rounded-md border border-tcs-border bg-tcs-bg p-3">
      <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
        <Link2 className="h-3.5 w-3.5" /> Setup portal
      </h4>
      <p className="text-[11px] text-tcs-text-muted">
        Send the sub a secure link to confirm business info, upload COI/W-9,
        submit the ACH payment authorization, and sign the service agreement — no login needed.
      </p>

      {steps && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {STEP_LABELS.map(({ key, label }) => (
            <span
              key={key}
              className={`rounded-md px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide ${
                steps[key]
                  ? 'bg-st-ok-bg-strong text-st-ok-fg-strong'
                  : 'bg-tcs-surface text-tcs-text-muted'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {activeToken && (
        <p className="mt-2 text-[11px] text-tcs-text-secondary">
          Active link ({activeToken.token_id}) sent to{' '}
          {activeToken.recipient_email ?? '—'}, expires{' '}
          {new Date(activeToken.expires_at).toLocaleDateString()}.
        </p>
      )}

      {error && <p className="mt-2 text-[11px] text-st-danger-fg">{error}</p>}

      {manualUrl && (
        <div className="mt-2 rounded-md border border-st-warn-border bg-st-warn-bg p-2 text-[11px] text-st-warn-fg-strong">
          Email delivery isn't configured — share this link with the sub directly:
          <div className="mt-1 flex items-center gap-1">
            <code className="block flex-1 truncate rounded bg-tcs-raised px-2 py-1 text-[10px]">
              {manualUrl}
            </code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(manualUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="rounded border border-st-warn-border-strong bg-tcs-raised p-1"
              aria-label="Copy link"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            {copied && <span className="text-[10px]">Copied</span>}
          </div>
        </div>
      )}

      {canWrite && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Sub's email (optional — uses record email)"
            className="block flex-1 rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs"
          />
          <button
            type="button"
            disabled={busy}
            onClick={handleIssue}
            className="inline-flex items-center gap-1 rounded-md bg-tcs-inverse px-3 py-1.5 text-xs font-semibold text-tcs-inverse-ink disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {activeToken ? 'Re-send link' : 'Send link'}
          </button>
        </div>
      )}
    </section>
  );
}
